<p-toast></p-toast>
@if (isLoading) {
    <p-progressBar mode="indeterminate"></p-progressBar>
}

<app-page-shell>

    <app-page-hero
        eyebrow="Shipments"
        title="Generate Deliveries"
        [subtitle]="'Create delivery assignments for ' + (shipment?.shipmentNumber || 'this shipment') + ' from available fleet resources.'">
        <div actions>
            <p-menu #assignmentActions [model]="visibleAssignmentActionItems" [popup]="true" appendTo="body"></p-menu>
            <p-button label="Quick onboard" icon="pi pi-sparkles" severity="secondary" [outlined]="true" (click)="openQuickFleetOnboarding()"></p-button>
            <p-button label="Add Assignment" icon="pi pi-plus" (click)="addAssignment()"></p-button>
            <p-button label="Equal Split" icon="fa-solid fa-scale-balanced" [outlined]="true" severity="secondary"
                (click)="equalSplitPayloadAllocations()" [disabled]="assignments.length === 0 || shipmentPayloadItems().length === 0"></p-button>
            <p-button [label]="t('pages.generateDeliveries.actions.moreActions', 'More Actions')" icon="fa-solid fa-ellipsis" [outlined]="true" severity="secondary"
                (click)="assignmentActions.toggle($event)"></p-button>
            <p-button label="Generate Deliveries" icon="pi pi-send" [loading]="deliveryGenerationInProgress" (click)="generateDeliveries()" [disabled]="deliveryForm.invalid"></p-button>
        </div>
    </app-page-hero>

    <div class="assignment-count-banner" [ngClass]="assignmentCountStatusClass()" role="status" aria-live="polite">
        <i [class]="assignmentCountStatusIcon()"></i>
        <span>{{ assignmentCountStatusMessage() }}</span>
    </div>

    @if (shipmentEquipmentSuggestionLabel(); as equipmentSuggestion) {
        <div class="equipment-suggestion-banner" role="status">
            <span class="equipment-suggestion-banner__icon"><i class="fa-solid fa-truck-front"></i></span>
            <div>
                <strong>Shipment equipment suggestion</strong>
                <span>{{ equipmentSuggestion }}</span>
                <small>Choose from the available fleet. The selected delivery vehicle remains the operational source of truth.</small>
            </div>
        </div>
    }

    <div class="assignment-count-banner assignment-count-banner--neutral">
        <div class="flex flex-column lg:flex-row gap-3 lg:align-items-center lg:justify-content-between w-full">
            <div class="flex flex-column gap-2">
                <strong>Assignment Resource Scope</strong>
                <span>{{ isSisterMode ? 'Showing sister-company drivers and trucks for assignment.' : 'Showing current-company drivers and trucks from all fleet sources.' }}</span>
            </div>
            <div class="flex flex-column md:flex-row gap-2 md:align-items-center">
                <p-selectButton
                    [options]="resourceViewOptions"
                    [ngModel]="resourceView"
                    (ngModelChange)="onResourceViewChange($event)">
                </p-selectButton>
                @if (isSisterMode) {
                    <p-select
                        [options]="sisterCompanies"
                        [appendTo]="'body'"
                        optionLabel="name"
                        optionValue="id"
                        [showClear]="true"
                        [placeholder]="sisterCompanyAllLabel"
                        [ngModel]="selectedSisterCompanyId"
                        (ngModelChange)="onSisterCompanyFilterChange($event)">
                    </p-select>
                }
            </div>
        </div>
    </div>

    <form [formGroup]="deliveryForm">
        <div class="p-fluid shipment-form-stack">
            <!-- Assignments Form Array -->
            <div formArrayName="assignments" class="assignment-list">
                <app-page-form-section
                    *ngFor="let assignment of assignments.controls; let i = index"
                    [formGroupName]="i"
                    icon="fa-solid fa-truck-fast"
                    [title]="assignmentTypeLabel(i) + ' #' + (i + 1)"
                    subtitle="Assign driver, vehicle, and optional container tracking details.">
                    <div actions>
                        <p-tag [value]="assignmentTypeTag(i)" [severity]="assignmentTypeSeverity(i)"></p-tag>
                        <p-button icon="pi pi-trash" [text]="true" severity="danger" (click)="removeAssignment(i)"></p-button>
                    </div>
                    <!-- Driver and Vehicle -->
                    <div class="formgrid grid">
                        @if (shipmentContainers.length) {
                        <div class="field col-12">
                            <p-inputGroup><p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon><p-iftaLabel>
                                <p-multiSelect [inputId]="'delivery-containers-' + i" formControlName="containerCaseIds" [options]="shipmentContainers" optionLabel="containerNumber" optionValue="id" display="chip" [showClear]="true" [loading]="containersLoading" appendTo="body" placeholder="Choose containers" styleClass="w-full"></p-multiSelect>
                                <label [for]="'delivery-containers-' + i">Containers for this delivery</label>
                            </p-iftaLabel></p-inputGroup>
                        </div>
                        }
                        @if (assignment.get('assignmentType')?.value === 'transporter') {
                        <div class="field col-12">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building-truck"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select
                                        [inputId]="'transporter-' + i"
                                        [options]="transporterOptions"
                                        formControlName="transporterCrmClientId"
                                        optionLabel="transporterName"
                                        optionValue="crmClientId"
                                        [filter]="true"
                                        filterBy="transporterName,crmClientNumber,linkedCompanyName,transportationAgencyName"
                                        [loading]="transporterOptionsLoading"
                                        [showClear]="true"
                                        appendTo="body"
                                        placeholder="Select Transporter"
                                        (onChange)="onAssignmentTransporterChange(i, $event.value)"
                                        styleClass="w-full">
                                    </p-select>
                                    <label [for]="'transporter-' + i">Transporter *</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="text-color-secondary">The selected driver and vehicle must both belong to this transporter.</small>
                        </div>
                        }
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-user-tie"></i></p-inputGroupAddon>
                                <p-iftaLabel class="asset-select-ifta">
                                    <p-select
                                        [options]="availableDrivers"
                                        formControlName="driver"
                                        [appendTo]="'body'"
                                        panelStyleClass="asset-select-panel"
                                        placeholder="Select Driver"
                                        optionLabel="optionLabel"
                                        dataKey="tableRowKey"
                                        [filter]="true"
                                        filterBy="searchText"
                                        filterPlaceholder="Search drivers"
                                        scrollHeight="260px"
                                        [loading]="driverOptionsLoading"
                                        (onShow)="retryFleetOptions()"
                                        styleClass="w-full asset-select-control">
                                        <ng-template let-driver pTemplate="selectedItem">
                                            @if (driver) {
                                            <div class="asset-selected-card">
                                                <div class="asset-selected-inline">
                                                    <strong class="asset-selected-name">{{ driverOptionLabel(driver) }}</strong>
                                                    <p-tag [value]="availabilityStatusLabel(driverAvailability(driver))" [severity]="availabilitySeverity(driverAvailability(driver))"></p-tag>
                                                </div>
                                                <small class="asset-selected-subline">{{ availabilitySummaryLine(driverAvailability(driver)) }} | {{ availabilityFifoLine(driverAvailability(driver)) }}</small>
                                            </div>
                                            } @else {
                                                <span>Select Driver</span>
                                            }
                                        </ng-template>
                                        <ng-template let-driver pTemplate="item">
                                            @if (driver) {
                                            <div class="asset-option-card">
                                                <div class="asset-option-head">
                                                    <div class="asset-primary">
                                                        <strong>{{ driverOptionLabel(driver) }}</strong>
                                                        <span>{{ driverOwnerLine(driver) }}</span>
                                                    </div>
                                                    <p-tag [value]="availabilityStatusLabel(driverAvailability(driver))" [severity]="availabilitySeverity(driverAvailability(driver))"></p-tag>
                                                </div>
                                                <div class="asset-metric-grid">
                                                    <div class="asset-metric">
                                                        <label>Source</label>
                                                        <strong>{{ driverAgencySourceLine(driver) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>Availability</label>
                                                        <strong>{{ availabilitySummaryLine(driverAvailability(driver)) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>Current Place</label>
                                                        <strong>{{ availabilityLocationLine(driverAvailability(driver)) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>FIFO Priority</label>
                                                        <strong>{{ availabilityFifoLine(driverAvailability(driver)) }}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            }
                                        </ng-template>
                                    </p-select>
                                    <label>Driver *</label>
                                </p-iftaLabel>
                                <p-inputGroupAddon><p-button icon="pi pi-plus" [text]="true" ariaLabel="Quick onboard driver and vehicle" (click)="openQuickFleetOnboarding(i)"></p-button></p-inputGroupAddon>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
                                <p-iftaLabel class="asset-select-ifta">
                                    <p-select
                                        [options]="availableVehicles"
                                        formControlName="vehicle"
                                        [appendTo]="'body'"
                                        panelStyleClass="asset-select-panel shipment-vehicle-select-panel"
                                        [panelStyle]="{ width: 'min(52rem, calc(100vw - 2rem))', maxWidth: 'calc(100vw - 2rem)' }"
                                        placeholder="Select Vehicle"
                                        optionLabel="optionLabel"
                                        dataKey="tableRowKey"
                                        [filter]="true"
                                        filterBy="searchText"
                                        filterPlaceholder="Search vehicles"
                                        scrollHeight="260px"
                                        [loading]="vehicleOptionsLoading"
                                        (onShow)="retryFleetOptions()"
                                        styleClass="w-full asset-select-control">
                                        <ng-template let-vehicle pTemplate="selectedItem">
                                            @if (vehicle) {
                                            <div class="asset-selected-card">
                                                <div class="asset-selected-inline">
                                                    <strong class="asset-selected-name">{{ vehicleOptionLabel(vehicle) }}</strong>
                                                    @if (isSuggestedVehicle(vehicle)) {
                                                    <p-tag value="Suggested" severity="info"></p-tag>
                                                    }
                                                    <p-tag [value]="availabilityStatusLabel(vehicleAvailability(vehicle))" [severity]="availabilitySeverity(vehicleAvailability(vehicle))"></p-tag>
                                                </div>
                                                <small class="asset-selected-subline">{{ availabilitySummaryLine(vehicleAvailability(vehicle)) }} | {{ availabilityFifoLine(vehicleAvailability(vehicle)) }}</small>
                                            </div>
                                            } @else {
                                                <span>Select Vehicle</span>
                                            }
                                        </ng-template>
                                        <ng-template let-vehicle pTemplate="item">
                                            @if (vehicle) {
                                            <div class="asset-option-card">
                                                <div class="asset-option-head">
                                                    <div class="asset-primary">
                                                        <strong>{{ vehicleOptionLabel(vehicle) }}</strong>
                                                    <span>{{ vehicleTypeLine(vehicle) }}</span>
                                                    </div>
                                                    @if (isSuggestedVehicle(vehicle)) {
                                                    <p-tag value="Matches shipment" severity="info"></p-tag>
                                                    }
                                                    <p-tag [value]="availabilityStatusLabel(vehicleAvailability(vehicle))" [severity]="availabilitySeverity(vehicleAvailability(vehicle))"></p-tag>
                                                </div>
                                                <div class="asset-metric-grid">
                                                    <div class="asset-metric">
                                                        <label>Identity</label>
                                                        <strong>{{ vehicleIdentityLine(vehicle) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>Ownership</label>
                                                        <strong>{{ vehicleOwnerLine(vehicle) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>Source</label>
                                                        <strong>{{ vehicleAgencySourceLine(vehicle) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>Availability</label>
                                                        <strong>{{ availabilitySummaryLine(vehicleAvailability(vehicle)) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>Current Place</label>
                                                        <strong>{{ availabilityLocationLine(vehicleAvailability(vehicle)) }}</strong>
                                                    </div>
                                                    <div class="asset-metric">
                                                        <label>FIFO Priority</label>
                                                        <strong>{{ availabilityFifoLine(vehicleAvailability(vehicle)) }}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            } @else {
                                            <div class="shipment-vehicle-option-placeholder">
                                                {{ vehicleOptionsError ? 'Vehicle page unavailable. Close and reopen to retry.' : 'Loading vehicle...' }}
                                            </div>
                                            }
                                        </ng-template>
                                        <ng-template pTemplate="empty">
                                            <div class="shipment-vehicle-option-placeholder">
                                                {{ vehicleOptionsLoading ? 'Loading vehicles...' : vehicleOptionsError ? 'Vehicles could not be loaded. Close and reopen to retry.' : 'No vehicles available for this selection.' }}
                                            </div>
                                        </ng-template>
                                    </p-select>
                                    <label>Vehicle *</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            @if (vehicleSelectionMessage(i); as vehicleMessage) {
                            <small class="vehicle-selection-message">{{ vehicleMessage }}</small>
                            }
                        </div>
                    </div>

                    <div class="formgrid grid mt-3">
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-file-invoice"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input [id]="'waybill-number-' + i" pInputText formControlName="waybillNumber" maxlength="100"
                                        placeholder="Enter only for an existing or historical waybill">
                                    <label [for]="'waybill-number-' + i">Waybill Number</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="text-color-secondary">A Cargomate-generated waybill replaces this value.</small>
                        </div>
                    </div>

                    <div class="assignment-transport-cost mt-3">
                        <div class="assignment-transport-cost__heading">
                            <div>
                                <strong>Transport Cost</strong>
                                <span>Driver type decides the accounting channel. Selling price stays on the shipment.</span>
                            </div>
                            <p-tag [value]="assignmentCostLabel(i)"
                                [severity]="!assignment.get('driver')?.value ? 'info' : assignmentUsesInternalCost(i) ? 'success' : 'warn'"></p-tag>
                        </div>
                        <div class="formgrid grid">
                            <div class="field col-12 md:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-coins"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-inputNumber
                                            [inputId]="'transport-cost-' + i"
                                            formControlName="transportCostAmount"
                                            mode="currency"
                                            [currency]="assignment.get('transportCostCurrencyCode')?.value || shipment?.currencyCode || 'SAR'"
                                            [min]="0"
                                            styleClass="w-full">
                                        </p-inputNumber>
                                        <label [for]="'transport-cost-' + i">Confirmed Transport Cost</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 assignment-transport-cost__hint">
                                <i class="fa-solid fa-circle-info"></i>
                                <span>{{ assignmentCostMessage(i) }}</span>
                            </div>
                        </div>
                    </div>

                    <p-fieldset legend="Trip money" [toggleable]="true" [collapsed]="true" class="block mt-3" formGroupName="tripMoney">
                        <div class="flex align-items-center justify-content-between gap-3 mb-3">
                            <div>
                                <strong>Driver trip earning and optional cash request</strong>
                                <small class="block text-color-secondary">Choose why cash is needed. Assignment does not issue cash.</small>
                            </div>
                            <p-checkbox [inputId]="'trip-enabled-' + i" formControlName="enabled" [binary]="true"></p-checkbox>
                        </div>
                        @if (assignment.get('tripMoney.enabled')?.value !== false) {
                        <div class="formgrid grid">
                            <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon><p-iftaLabel>
                                <p-select [inputId]="'driver-pay-method-' + i" formControlName="payMethod" [options]="driverPayMethods" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                <label [for]="'driver-pay-method-' + i">Driver pay method</label>
                            </p-iftaLabel></p-inputGroup></div>
                            @if (assignment.get('tripMoney.quotedDriverRate')?.value) { <small class="field col-12">Matched rate: {{ assignment.get('tripMoney.quotedDriverRate')?.value | number:'1.2-2' }} {{ shipment?.currencyCode || 'SAR' }} {{ assignment.get('tripMoney.payMethod')?.value === 'PER_KM' ? 'per km; pay = rate × approved km' : 'per trip' }}. Driver pay is separate from transport cost and cash requests.</small> }
                            @if (assignment.get('tripMoney.payMethod')?.value === 'PER_KM') {
                                @if (shipment?.distance && assignment.get('tripMoney.ratePerKm')?.value) { <small class="field col-12">Planned estimate: {{ (shipment.distance || 0) * assignment.get('tripMoney.ratePerKm')?.value | number:'1.2-2' }} {{ shipment.currencyCode || 'SAR' }} for {{ shipment.distance | number:'1.1-1' }} km. Final pay uses the reviewed distance source.</small> }
                                <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calculator"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-inputNumber [inputId]="'driver-km-rate-' + i" formControlName="ratePerKm" [min]="0.01" [minFractionDigits]="2" styleClass="w-full"></p-inputNumber>
                                    <label [for]="'driver-km-rate-' + i">Pay per km (required before Accounts)</label>
                                </p-iftaLabel></p-inputGroup></div>
                                <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'driver-distance-source-' + i" formControlName="distanceSource" [options]="payDistanceSources" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                    <label [for]="'driver-distance-source-' + i">Distance evidence source *</label>
                                </p-iftaLabel></p-inputGroup></div>
                                <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'driver-start-place-' + i" pInputText formControlName="expectedStartPlace"><label [for]="'driver-start-place-' + i">Pay trip start (before pickup)</label>
                                </p-iftaLabel></p-inputGroup></div>
                                <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'driver-parking-place-' + i" pInputText formControlName="expectedParkingPlace"><label [for]="'driver-parking-place-' + i">Pay trip end (approved parking)</label>
                                </p-iftaLabel></p-inputGroup></div>
                                <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'driver-trip-group-' + i" pInputText formControlName="payTripGroupKey"><label [for]="'driver-trip-group-' + i">Trip group (same shipment only)</label>
                                </p-iftaLabel></p-inputGroup></div>
                                @if (assignment.get('tripMoney')?.hasError('driverRateRequired')) { <small class="field col-12 text-red-500">Choose the distance source.</small> }
                            }
                            @if (assignment.get('tripMoney.payMethod')?.value === 'FIXED_TRIP' || assignment.get('tripMoney.payMethod')?.value === 'CITY_PAIR_TRIP') {
                            <div class="field col-12 md:col-4">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-inputNumber [inputId]="'trip-earning-' + i" formControlName="tripEarningAmount" mode="currency" [currency]="assignment.get('tripMoney.currencyCode')?.value || shipment?.currencyCode || 'SAR'" styleClass="w-full"></p-inputNumber>
                                    <label [for]="'trip-earning-' + i">Trip earning override</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            }
                            <div class="field col-12 md:col-4">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-inputNumber [inputId]="'trip-cash-' + i" formControlName="tripCashAmount" mode="currency" [currency]="assignment.get('tripMoney.currencyCode')?.value || shipment?.currencyCode || 'SAR'" styleClass="w-full"></p-inputNumber>
                                    <label [for]="'trip-cash-' + i">Cash amount (blank uses configured default)</label>
                                </p-iftaLabel></p-inputGroup>
                                <p-inputGroup class="mt-2"><p-inputGroupAddon><i class="pi pi-list"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'trip-cash-purpose-' + i" formControlName="cashPurpose" [options]="cashPurposeOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                    <label [for]="'trip-cash-purpose-' + i">Cash purpose</label>
                                </p-iftaLabel></p-inputGroup>
                                @if (assignment.get('tripMoney.cashPurpose')?.value !== 'NONE') {
                                <p-inputGroup class="mt-2"><p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [inputId]="'trip-cash-box-' + i" formControlName="pettyCashBoxId" [options]="availableCashBoxes(assignment.get('tripMoney.currencyCode')?.value)" optionLabel="boxName" optionValue="pettyCashBoxId" [filter]="true" filterBy="boxName,locationName" appendTo="body" styleClass="w-full">
                                        <ng-template pTemplate="item" let-box>{{ box.boxName }} · {{ box.locationName || 'Location not recorded' }}</ng-template>
                                        <ng-template pTemplate="selectedItem" let-box>{{ box?.boxName }} · {{ box?.locationName }}</ng-template>
                                    </p-select>
                                    <label [for]="'trip-cash-box-' + i">Cash from box / location *</label>
                                </p-iftaLabel></p-inputGroup>
                                @if (cashBoxError) { <small class="block text-red-500">{{ cashBoxError }}</small> }
                                @if (assignment.get('tripMoney')?.hasError('cashBoxRequired')) { <small class="block text-red-500">Choose an active cash box before generating this delivery.</small> }
                                }
                            </div>
                            <div class="field col-12 md:col-4">
                                <p-inputGroup><p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'trip-reason-' + i" pInputText formControlName="reason" maxlength="500"><label [for]="'trip-reason-' + i">Override reason</label>
                                </p-iftaLabel></p-inputGroup>
                                <small class="text-color-secondary">Leave amounts empty to use the Driver trip rate card. A reason is required for an override.</small>
                                @if (assignment.get('tripMoney')?.hasError('overrideReasonRequired')) {
                                    <small class="block text-red-500">Enter a reason for the trip-money override.</small>
                                }
                            </div>
                        </div>
                        }
                    </p-fieldset>

                    @if (getLaborSelections(i).length) {
                    <p-fieldset legend="Workforce" [toggleable]="true" [collapsed]="false" class="block mt-3">
                        <small class="block text-color-secondary mb-3">Optional staffing for this delivery. The driver is preferred automatically when qualified; leave any row empty to assign it later.</small>
                        <div formArrayName="laborSelections" class="flex flex-column gap-2">
                            @for (selection of getLaborSelections(i).controls; track $index; let slotIndex = $index) {
                            <div [formGroupName]="slotIndex" class="formgrid grid align-items-end surface-ground border-round p-2">
                                <div class="field col-12 md:col-4 mb-0">
                                    <label class="block text-sm mb-1">{{ selection.get('roleName')?.value }} · Slot {{ selection.get('slotNumber')?.value }}</label>
                                    <p-select [options]="laborCandidates(selection.get('fieldRoleId')?.value, i, slotIndex)" formControlName="fieldWorkerId"
                                        optionLabel="displayName" optionValue="fieldWorkerId" [filter]="true" [showClear]="true"
                                        appendTo="body" styleClass="w-full" placeholder="Assign later">
                                        <ng-template let-candidate pTemplate="item">
                                            <div class="flex flex-column"><strong>{{ candidate.displayName }}</strong><small>{{ candidate.sourceType }}{{ candidate.eligible ? '' : ' · review eligibility/rate' }}</small></div>
                                        </ng-template>
                                    </p-select>
                                </div>
                                <div class="field col-6 md:col-2 mb-0">
                                    <p-iftaLabel>
                                        <p-inputNumber [inputId]="'labor-buy-' + i + '-' + slotIndex" formControlName="buyRateSnapshot"
                                            [min]="0" mode="currency" [currency]="selection.get('currencyCode')?.value || shipment?.currencyCode || 'SAR'" styleClass="w-full"></p-inputNumber>
                                        <label [for]="'labor-buy-' + i + '-' + slotIndex">Manual buy rate</label>
                                    </p-iftaLabel>
                                </div>
                                <div class="field col-6 md:col-2 mb-0">
                                    <p-iftaLabel>
                                        <p-select [inputId]="'labor-unit-' + i + '-' + slotIndex" [options]="['HOUR','DAY','TASK','FLAT']" formControlName="billingUnit" styleClass="w-full"></p-select>
                                        <label [for]="'labor-unit-' + i + '-' + slotIndex">Buy unit</label>
                                    </p-iftaLabel>
                                </div>
                                <div class="field col-10 md:col-3 mb-0">
                                    <p-iftaLabel>
                                        <input [id]="'labor-reason-' + i + '-' + slotIndex" pInputText formControlName="overrideReason" maxlength="500">
                                        <label [for]="'labor-reason-' + i + '-' + slotIndex">Manual-rate reason</label>
                                    </p-iftaLabel>
                                </div>
                                <div class="field col-2 md:col-1 mb-0 text-right">
                                    <p-button icon="pi pi-plus" [text]="true" ariaLabel="Create laborer"
                                        (onClick)="openLaborerQuickCreate(i, slotIndex)"></p-button>
                                </div>
                            </div>
                            }
                        </div>
                    </p-fieldset>
                    }

                    @if (assignment.get('driver')?.value) {
                    <div class="hr-readiness-preview">
                        <div class="hr-readiness-preview__icon">
                            <i class="fa-solid fa-clipboard-check"></i>
                        </div>
                        <div class="hr-readiness-preview__body">
                            <div class="hr-readiness-preview__title">
                                <strong>HR Workforce Readiness</strong>
                                <p-tag
                                    [value]="assignmentDriverReadinessStatusLabel(i)"
                                    [severity]="readinessSeverity(assignmentDriverReadiness(i)?.readinessStatus)">
                                </p-tag>
                            </div>
                            <span>{{ assignmentDriverReadinessSummary(i) }}</span>
                            @if (assignmentDriverReadiness(i)) {
                                <small>{{ assignmentDriverReadinessIssue(i) }}</small>
                            }
                        </div>
                    </div>
                    }

                    <div class="mt-3">
                        <app-page-form-section
                            icon="pi pi-box"
                            title="Payload Allocation"
                            [subtitle]="'Assigned payload from the original shipment. ' + payloadAllocationSummary()">
                            @if (shipmentPayloadItems().length > 0) {
                            <div formArrayName="payloadItems">
                                <p-table
                                    [value]="getAssignmentPayloadItems(i).controls"
                                    styleClass="p-datatable-sm"
                                    responsiveLayout="scroll">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>Payload Item</th>
                                            <th>Shipment Qty</th>
                                            <th>Assigned Qty</th>
                                            <th>Assigned Weight</th>
                                            <th>Remaining</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-payloadControl let-payloadIndex="rowIndex">
                                        <tr [formGroupName]="payloadIndex">
                                            <td>
                                                <div class="flex flex-column gap-1">
                                                    <span class="font-semibold">{{ payloadLineLabel(shipmentPayloadItems()[payloadIndex]) }}</span>
                                                    <small>{{ payloadWeightLabel(shipmentPayloadItems()[payloadIndex]) }}</small>
                                                </div>
                                            </td>
                                            <td>{{ payloadQuantityLabel(shipmentPayloadItems()[payloadIndex]) }}</td>
                                            <td>
                                                <p-inputNumber
                                                    formControlName="quantity"
                                                    [min]="0"
                                                    [max]="maxQuantityForAllocation(i, payloadIndex)"
                                                    [showButtons]="true"
                                                    [useGrouping]="false"
                                                    styleClass="w-full"
                                                    (onInput)="onPayloadQuantityChange(i, payloadIndex)"
                                                    (onBlur)="onPayloadQuantityChange(i, payloadIndex)">
                                                </p-inputNumber>
                                            </td>
                                            <td>{{ allocationWeightLabel(i, payloadIndex) }}</td>
                                            <td>{{ remainingQuantityForPayload(payloadIndex) }} {{ shipmentPayloadItems()[payloadIndex]?.itemQuantity?.uom?.name || '' }}</td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            </div>
                            } @else {
                                <div class="shipment-empty-state border-1 border-dashed border-round p-4">
                                    <i class="pi pi-info-circle"></i>
                                    <strong>No shipment payload items</strong>
                                    <span>This shipment does not have payload lines to allocate.</span>
                                </div>
                            }
                        </app-page-form-section>
                    </div>

                    @if (hasDeliveryFlexFields()) {
                    <div class="mt-3">
                        <app-page-form-section
                            icon="pi pi-sliders-h"
                            title="Delivery Flex Fields"
                            subtitle="Company-specific delivery references and identifiers.">
                            <app-flex-field-form
                                [flexFields]="getAssignmentFlexFields(i)"
                                [objectName]="'SHIPMENT_DELIVERY'"
                                [owningCompanyId]="deliveryFlexOwningCompanyId"
                                [excludedFieldNames]="legacyWaybillFlexFieldNames"
                                [containerClass]="'formgrid grid'"
                                [columnClass]="'field col-12 md:col-6'">
                            </app-flex-field-form>
                        </app-page-form-section>
                    </div>
                    }

                </app-page-form-section>

                <!-- Empty Message -->
                @if (assignments.controls.length === 0) {
                <div class="shipment-empty-state border-1 border-dashed border-round p-5">
                    <i class="pi pi-info-circle"></i>
                    <strong>No assignments created</strong>
                    <span>Click Add Assignment to begin.</span>
                </div>
                }
            </div>
        </div>
    </form>
</app-page-shell>

<app-quick-fleet-onboarding-dialog
    [(visible)]="quickFleetOnboardingVisible"
    [defaultSource]="assignmentSource"
    [defaultTransporterCrmClientId]="selectedTransporterCrmClientId"
    (completed)="onQuickFleetOnboarded($event)">
</app-quick-fleet-onboarding-dialog>

<app-party-quick-create-dialog [(visible)]="laborerQuickCreateVisible" mode="laborer" [companyRequired]="false"
    (created)="onLaborerQuickCreated($event)">
</app-party-quick-create-dialog>

<p-dialog
    header="Create Driver"
    [(visible)]="driverDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(1100px, 96vw)' }"
    [contentStyle]="{ padding: '0' }">
    <app-edit-driver
        [embedded]="true"
        [embeddedSource]="assignmentSource"
        (saved)="onDriverCreated($event)"
        (cancelled)="driverDialogVisible = false">
    </app-edit-driver>
</p-dialog>

<p-dialog
    header="Add Mobilized Assignment"
    [(visible)]="mobilizationDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(1100px, 96vw)' }">
    <div class="flex flex-column gap-3">
        <div class="flex flex-column md:flex-row gap-2 md:align-items-center">
            <span class="p-input-icon-left flex-1">
                <i class="pi pi-search"></i>
                <input
                    pInputText
                    class="w-full"
                    [value]="mobilizationKeyword"
                    (input)="onMobilizationKeywordInput($event)"
                    (keyup.enter)="searchMobilizations()"
                    placeholder="Search driver, vehicle, project, site" />
            </span>
            <div class="flex gap-2">
                <p-button label="Search" icon="pi pi-search" (click)="searchMobilizations()"></p-button>
                <p-button label="Clear" icon="pi pi-times" [outlined]="true" severity="secondary" (click)="clearMobilizationSearch()"></p-button>
            </div>
        </div>

        <p-table
            [value]="mobilizations"
            [lazy]="true"
            [paginator]="true"
            [rows]="mobilizationsRowsPerPage"
            [first]="mobilizationsFirst"
            [totalRecords]="mobilizationsTotalRecords"
            [loading]="mobilizationsLoading"
            [rowsPerPageOptions]="[10, 20, 50]"
            (onLazyLoad)="loadMobilizations($event)"
            styleClass="p-datatable-sm"
            responsiveLayout="scroll">
            <ng-template pTemplate="header">
                <tr>
                    <th>Mobilization</th>
                    <th>Driver</th>
                    <th>Vehicle</th>
                    <th>Project/Site</th>
                    <th>Dates</th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <td>
                        <div class="flex flex-column gap-1">
                            <span class="font-semibold">{{ row.mobilizationNumber || ('MOB-' + row.mobilizationId) }}</span>
                            <p-tag value="ACTIVE" severity="success"></p-tag>
                        </div>
                    </td>
                    <td>
                        <div class="flex flex-column gap-1">
                            <span>{{ row.driverName || '-' }}</span>
                            <small>{{ row.driverPhoneNumber || row.driverLicenseNumber || '-' }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="flex flex-column gap-1">
                            <span>{{ row.vehiclePlateNumber || '-' }}</span>
                            <small>{{ row.vehicleRegistrationNumber || row.vehicleTypeName || '-' }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="flex flex-column gap-1">
                            <span>{{ mobilizationProjectLabel(row) }}</span>
                            <small>{{ mobilizationSiteLabel(row) }}</small>
                        </div>
                    </td>
                    <td>{{ row.startDate | date:'mediumDate' }} - {{ row.endDate | date:'mediumDate' }}</td>
                    <td class="text-right">
                        <p-button label="Select" icon="pi pi-check" size="small" (click)="selectMobilization(row)"></p-button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="6" class="text-center p-4">No active mobilizations found.</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</p-dialog>

<p-dialog
    header="Bulk Upload Deliveries"
    [(visible)]="deliveryImportDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(1180px, 96vw)' }">
    <div class="flex flex-column gap-3">
        <div class="flex flex-column md:flex-row gap-2 md:align-items-center">
            <label class="p-button p-component p-button-secondary p-button-outlined">
                <i class="pi pi-upload mr-2"></i>
                <span>{{ deliveryImportFileName || 'Choose XLSX or CSV' }}</span>
                <input type="file" accept=".xlsx,.xls,.csv" class="hidden" (change)="onDeliveryImportFileSelected($event)" />
            </label>
            <p-button label="Download Template" icon="pi pi-download" severity="secondary" [outlined]="true" (click)="downloadDeliveryImportTemplate()"></p-button>
            <span class="text-sm text-color-secondary">
                Valid rows: {{ deliveryImportValidCount() }} | Invalid rows: {{ deliveryImportInvalidCount() }}
            </span>
        </div>

        @if (deliveryImportLoading) {
            <p-progressBar mode="indeterminate"></p-progressBar>
        }

        <p-table
            [value]="deliveryImportRows"
            styleClass="p-datatable-sm"
            responsiveLayout="scroll"
            [paginator]="true"
            [rows]="8"
            [rowsPerPageOptions]="[8, 15, 30]">
            <ng-template pTemplate="header">
                <tr>
                    <th>Row</th>
                    <th>Status</th>
                    <th>Driver</th>
                    <th>Vehicle</th>
                    <th>Payload</th>
                    <th>Flex Fields</th>
                    <th>Errors</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <td>{{ row.rowNumber }}</td>
                    <td><p-tag [value]="row.status" [severity]="importStatusSeverity(row.status)"></p-tag></td>
                    <td>
                        <div class="flex flex-column gap-1">
                            <span>{{ row.driver?.name || '-' }}</span>
                            <small>{{ row.driver?.phoneNumber || row.values?.driverPhoneNumber || '-' }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="flex flex-column gap-1">
                            <span>{{ row.vehicle?.plateNumber || row.values?.vehiclePlateNumber || '-' }}</span>
                            <small>{{ row.vehicle?.registrationNumber || row.values?.vehicleRegistrationNumber || '-' }}</small>
                        </div>
                    </td>
                    <td>{{ importPayloadSummary(row) }}</td>
                    <td>{{ importFlexSummary(row) }}</td>
                    <td>
                        <span class="text-red-500">{{ row.errors.length ? row.errors.join('; ') : '-' }}</span>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="7" class="text-center p-4">Upload a delivery assignment file to preview rows.</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="deliveryImportDialogVisible = false"></p-button>
        <p-button label="Add Valid Rows" icon="pi pi-check" [disabled]="deliveryImportValidCount() === 0" (click)="addValidImportedAssignments()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    header="Create Vehicle"
    [(visible)]="vehicleDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(1100px, 96vw)' }"
    [contentStyle]="{ padding: '0' }">
    <app-edit-vehicle
        [embedded]="true"
        [embeddedSource]="assignmentSource"
        (saved)="onVehicleCreated($event)"
        (cancelled)="vehicleDialogVisible = false">
    </app-edit-vehicle>
</p-dialog>
